import { Edit01, SearchMd, Table as TableIcon, Trash01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useMessageTemplatesCount, usePaginatedMessageTemplates } from "@/api/message-templates";
import { usePagination } from "@/api/pagination";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { Input } from "@/components/base/input/input";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useSort } from "@/hooks/use-sort";
import { pluralize } from "@/utils/text";
import { DeleteTemplateDialog } from "./delete-template-dialog";
import { TemplateEditorDialog } from "./template-editor-dialog";

type TemplateRow = NonNullable<ReturnType<typeof usePaginatedMessageTemplates>["data"]>[number];

export function MessageTemplatesList() {
  const [search, setSearch] = useState("");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<"name" | "category" | "createdAt">("createdAt");

  const [editTemplateId, setEditTemplateId] = useState<string | null>(null);
  const [deleteTemplate, setDeleteTemplate] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    pagination.setPage(1);
  }, [debouncedSearch]);

  const { data: templates = [], isLoading } = usePaginatedMessageTemplates({
    pagination: pagination.pagination,
    search: debouncedSearch || undefined,
    sort: sort.column ? sort : undefined,
  });

  const { data: total = 0 } = useMessageTemplatesCount({
    search: debouncedSearch || undefined,
  });

  const totalPages = Math.ceil(total / pagination.pagination.limit);

  const columns = useMemo<UnboxTableColumn<TemplateRow>[]>(
    () => [
      {
        id: "name",
        label: "Name",
        sortable: true,
        minWidth: "60%",
        render: (_value, template) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-primary">{template.name}</span>
            <span className="text-xs text-tertiary truncate max-w-xs">{template.content.split("\n")[0]}</span>
          </div>
        ),
      },
      {
        id: "category",
        label: "Category",
        minWidth: "30%",
        render: (_value, template) => <span className="text-sm text-tertiary">{template.category}</span>,
      },
      {
        id: "actions",
        label: "",
        minWidth: "96px",
        render: (_value, template) => (
          <div className="flex items-center gap-1 justify-end">
            <button
              type="button"
              aria-label="Delete template"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTemplate({ id: template.id, name: template.name });
              }}
              className="p-1.5 rounded text-quaternary hover:text-error-600 transition-colors"
            >
              <Trash01 className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Edit template"
              onClick={(e) => {
                e.stopPropagation();
                setEditTemplateId(template.id);
              }}
              className="p-1.5 rounded text-quaternary hover:text-secondary transition-colors"
            >
              <Edit01 className="size-4" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 pt-6">
      <div className="flex flex-shrink-0 flex-row items-center justify-between pb-6 gap-4">
        <span className="text-sm font-semibold text-gray-500">{pluralize(total, "Template", "Templates")}</span>
        <Input
          size="sm"
          inputClassName="text-sm font-semibold"
          className="w-64"
          value={search}
          onClear={
            search
              ? () => {
                  setSearch("");
                  clearSearch();
                }
              : undefined
          }
          onChange={setSearch}
          placeholder="Search sequence name"
          icon={SearchMd}
        />
      </div>

      <div className="flex flex-col w-full flex-1 min-h-0">
        <UnboxTable<TemplateRow>
          aria-label="Message Templates"
          columns={columns}
          rows={isLoading ? undefined : templates}
          rowId="id"
          onClickRow={(row) => setEditTemplateId(row.id)}
          sorting={{ descriptor, onSortChange: setFromDescriptor }}
          pagination={{ page: pagination.page, totalPages, onPageChange: pagination.setPage }}
          emptyState={{ icon: TableIcon, title: "No templates found" }}
          className="max-h-[60vh]"
        />
      </div>

      <TemplateEditorDialog
        open={editTemplateId !== null}
        onOpenChange={(open) => {
          if (!open) setEditTemplateId(null);
        }}
        templateId={editTemplateId}
      />

      {deleteTemplate && (
        <DeleteTemplateDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDeleteTemplate(null);
          }}
          templateId={deleteTemplate.id}
          templateName={deleteTemplate.name}
        />
      )}
    </div>
  );
}
